//#region Imports
// ** React Imports
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

// ** MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Menu,
  MenuItem,
  FormLabel,
  FormControlLabel,
  Grid,
  Divider,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import dayjs from "dayjs";

// ** Util Imports
import { hexToRGBA } from "../../components/utils/hex-to-rgba";

// ** Custom Component Imports
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";
import DatePopover from "./component/DatePickerSelection";

// ** Dialog Imports
import SelectJobsDialog from "./component/SelectJobs";
import JobDetailDialog from "screens/WorkOrders/component/JobDetail";
import PreviewInvoiceDialog from "./component/PreviewInvoice";
import AttachmentUpload from "components/AttachmentUpload";
import AddSalesTaxDialog from "screens/Settings/component/AddSalesTaxDialog";


// ** Icon Imports
import Icon from "../../components/icon";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VideoPlaceholderIcon from 'images/video_placeholder.png';
import CSV_PlaceholderIcon from 'images/Attachment_Icons/CSV__PlaceholderIcon.png';
import PDF_PlaceholderIcon from 'images/Attachment_Icons/PDF_PlaceholderIcon.png';
import DOCX_PlaceholderIcon from 'images/Attachment_Icons/DOCX__PlaceholderIcon.png';
import XLSX_PlaceholderIcon from 'images/Attachment_Icons/XLSX__PlaceholderIcon.png';
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";
import FilePlaceholder from 'images/image_placeholder.png';

// ** Custom Components Imports
import OptionsMenu from "../../components/option-menu";
import CustomLabel from "components/CustomLabel";

// ** Third Party Components
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from "lodash";

// ** Logic Imports
import { useCreateInvoice } from "./logic/invoice-logic";
import { saveAs } from 'file-saver';
import { useInvoicePermission } from "./permissions/InvoicePermissions";
import { GetIndividualPayment } from "./logic/GetPayment";

// ** API Imports
import { GetJobs } from "./logic/GetJobs";
import { GetAttachments } from "components/logic/GetAttachments";
import { DownloadAttachments } from "components/logic/DownloadAttachments";
import { DeleteAttachment } from "components/logic/DeleteAttachment";
import { GetServiceRequestRaw, GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";
import PaymentsPage from "../Payments/component/PaymentsComponent";
//#endregion Imports

//#region Styled Components
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: "fit-content",
    border: "1px solid #E1E4EA",
  },
}));

const CustomChipWithDropdown = ({ tag, onSelect, hasInvoiceSavePermission }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedTag) => {
    onSelect(selectedTag);
    handleClose();
  };

  const isDropdownDisabled = tag === 'Paid' || !hasInvoiceSavePermission;

  return (
    <Box>
      <CustomChip
        rounded
        size="small"
        skin="light"
        sx={{ 
          lineHeight: 1, 
          "&:hover": !isDropdownDisabled ? { color: "#fff" } : {},
          cursor: !isDropdownDisabled ? 'pointer' : 'default'
        }}
        color={
          tag === "Paid"
            ? "success"
            : tag === "Partially Paid"
            ? "teal"
            : tag === "Sent"
            ? "violet"
            : tag === "Ready to Send"
            ? "info"
            : tag === "Payment Past Due"
            ? "error"
            : tag === "Missed Send Date"
            ? "yellow"
            : tag === "Canceled"
            ? "warning"
            : "secondary"
        }
        icon={<FiberManualRecordIcon fontSize="small" />}
        label={tag}
        {...(!isDropdownDisabled && {
          onClick: handleClick
        })}
      />
      {!isDropdownDisabled && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{ sx: { width: "max-content" } }}
        >
          <MenuItem 
            value='Canceled'
            onClick={() => handleSelect('Canceled')}
          >
            Canceled
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};
//#endregion Styled Components

const InvoiceDetail = () => {
  //#region Permissions
  const { InvoicePermissionProps } = useInvoicePermission();
  const {
    hasInvoiceSavePermission,
    hasInvoiceSendEmailPermission,
  } = InvoicePermissionProps;
  //#endregion Permissions

  //#region Props Destructuring
  const { JobSelectionProps, InvoiceCreationProps } = useCreateInvoice({
    toast,
    mode: 'detail'
  });
  const {
    handleOpenSelectJobsDlg,
    handleCloseSelectJobsDlg,
    invoiceType,
    selectedJobs
  } = JobSelectionProps;

  const {
    openSelectJobsDlg,
    columns,
    fetchInvoice,
    invoice,
    setInvoice,
    openGenerateInvoice,
    handleOpenGenerateInvoice,
    handleCloseGenerateInvoice,
    existingInvoice,
    setExistingInvoice,
    handleChangeInvoiceDetails,
    note,
    setNote,
    handleAddNote,
    handleDeleteNote,
    term,
    setTerm,
    handleAddTerm,
    selectedWorkReq,
    setSelectedWorkReq,
    selectedJob,
    setSelectedJob,
    fetchWorkRequirements,
    fetchJobs,
    fetchRecords,
    handleCloseJobDetail,
    openJobDetail,
    rules,
    handleChangeTerm,
    handleDeleteTerm,
    handleSaveInvoice,
    handlePageBack,
    loading,
    setLoading,
    openNotes,
    setOpenNotes,
    navigate,
    contentHeightFixed,
    handleTabChange,
    tabValue,
    filteredRows,
    setFilteredRows,
    salesTaxList,
    errorsDetails,
    handleOpenUploadDlg,
    handleCloseUploadDlg,
    openUploadDlg,
    setOpenUploadDlg,
    handleOpenCountyDlg,
    handleCloseCountyDlg,
    openCountyDlg,
    handleAddCounty,
    jobs
  } = InvoiceCreationProps;
  //#endregion Props Destructuring

  //#region Hooks and Vars
  const { invoiceID } = useParams();
  const theme = useTheme();
  //#endregion Hooks and Vars

  //#region States
  const [isChanged, setIsChanged] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [btnLoader, setBtnLoader] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);

  // Summary States
  const invID = invoice?.invoiceID;
  const reqID = invoice?.requestID;
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [advanceReceived, setAdvanceReceived] = useState(0);
  const [balanceRemaining, setBalanceRemaining] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [advanceUtilized, setAdvanceUtilized] = useState(0);
  const [invoiceStatus, setInvoiceStatus] = useState("");
  
  //#endregion states

  //#region Handlers
    useEffect(() => {
      const isEqual = _.isEqual(existingInvoice, invoice);
      setIsChanged(!isEqual); 
    }, [existingInvoice, invoice]);

    // Summary Styles
    const containerStyles = {
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center'
    };
  
    const summaryStyles = {
      width: { md: '50%', sm: '100%'}
    };
  
    const summaryItemStyles = {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4
    }
  
    const grandTotalStyles = {
      display: 'flex',
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mt: 4, 
      color: '#23A26D',
      fontWeight: 600,
      fontSize: '18px'
    }

  useEffect(() => {
    if (invoiceID) {
      fetchInvoice(invoiceID);
      fetchAttachments();
    }
  }, [invoiceID]);

  // Summary Logic
  useEffect(() => {
    const fetchPayment = async () => {
      try{
        const result = await GetServiceRequests(0, reqID,0, navigate);
        if(result && result.length > 0){
          const amountPaid = result[0].depositAmountPaid;
          const amountUtilized = result[0].depositUtilizedAmount;
          const difference = amountPaid - amountUtilized;
          setAdvanceReceived(difference);
          setBalanceRemaining(difference);
        }
      }catch(error){
        console.error("Error fetching payments:", error)
      }
    }
    if(invID || reqID){
      fetchPayment();
    }
  }, [invID, reqID, grandTotal])

  const parseAmount = (amount) => {
    return  parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) 
  };

  const calculatedValues = useMemo(() => {
    let calculatedTotalAmount = 0;
    let calculatedTotalTax = 0;
  
    const assessmentAmount = parseFloat(invoice?.assessmentAmount || 0);
    const depositAmount = parseFloat(invoice?.depositRequired || 0);
    const salesTax = parseFloat(invoice?.salesTax || 0);
    const discountAmount = parseFloat(invoice?.discountAmount || 0);
    const advanceReceived = parseFloat(invoice?.depositUtilizedAmount || 0);
    const invoiceJobs = invoice?.invoiceJobs || [];
  
    if (invoiceType === 'Assessment') {
      calculatedTotalAmount = assessmentAmount;
      calculatedTotalTax = salesTax ? calculatedTotalAmount * (salesTax / 100) : 0;
    } 
    else if (invoiceType === "WorkOrderDeposit") {
      calculatedTotalAmount = depositAmount;
      calculatedTotalTax = salesTax ? calculatedTotalAmount * (salesTax / 100) : 0;
    }
    else if (invoiceType === "WorkOrder") {
      calculatedTotalAmount = invoiceJobs.reduce((sum, job) => {
        return sum + parseFloat(job.totalAmount || 0);
      }, 0);
      calculatedTotalTax = salesTax ? calculatedTotalAmount * (salesTax / 100) : 0;
    }
  
    const calculatedDiscount = discountAmount;
    const subtotal = calculatedTotalAmount - calculatedDiscount;
    let calculatedGrandTotal = subtotal + calculatedTotalTax;
  
    let advanceUtilized = 0;
  
    if (advanceReceived && advanceReceived > 0) {
      if (advanceReceived >= calculatedGrandTotal) {
        advanceUtilized = calculatedGrandTotal;
        calculatedGrandTotal = 0;
      } else {
        advanceUtilized = advanceReceived;
        calculatedGrandTotal = calculatedGrandTotal - advanceReceived;
      }
    } else {
      advanceUtilized = 0;
    }
  
    return {
      totalAmount: calculatedTotalAmount,
      totalTax: calculatedTotalTax,
      totalDiscount: calculatedDiscount,
      grandTotal: calculatedGrandTotal,
      advanceUtilized: advanceUtilized
    };
  
  }, [
    invoiceType, 
    advanceReceived,
    invoice?.assessmentAmount,
    invoice?.depositAmount,
    invoice?.salesTax,
    invoice?.taxAmount,
    invoice?.discountAmount,
    invoice?.invoiceJobs,
    selectedJobs
  ]);
  
  const [summary, setSummary] = useState({
    totalAmount: 0,
    depositCreditUtilized: 0,
    totalTax: 0,
    grandTotal: 0
  })

  useEffect(() => {
    if (!invoice) return;
  
    setSummary(prev => {
      if (
        prev.totalAmount === calculatedValues.totalAmount &&
        prev.depositCreditUtilized === calculatedValues.advanceUtilized &&
        prev.totalTax === calculatedValues.totalTax &&
        prev.grandTotal === calculatedValues.grandTotal
      ) {
        return prev;
      }
  
      return {
        ...prev,
        totalAmount: calculatedValues.totalAmount,
        depositCreditUtilized: calculatedValues.advanceUtilized,
        totalTax: calculatedValues.totalTax,
        grandTotal: calculatedValues.grandTotal
      };
    });
  }, [calculatedValues]);

  const fetchAttachments = async () => {
    try {
      const response = await GetAttachments(
        "Attachment",
        "Invoice",
        invoiceID,
        0
      );
      setAttachments(response);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchThumbnails = async () => {
      const newThumbnails = {};
  
      for (const attachment of attachments) {
          const imgUrl = await getAttachmentThumbnail(attachment.fileName);
          newThumbnails[attachment.fileName] = imgUrl;
      }
  
      setThumbnails(newThumbnails);
    };
  
    fetchThumbnails();
  }, [attachments]);

  const getFileType = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) return "video";
    if (
      [
        "txt",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "csv",
        "json",
        "xml",
      ].includes(ext)
    )
      return "text";
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext))
      return "image";
    return "other";
  };

  const getAttachmentThumbnail = async (filename) => {
    const fileExtension = filename.split(".").pop()?.toLowerCase();

    const fileType = getFileType(filename);
    if (fileType === "video") {
      return VideoPlaceholderIcon;
    } else if (fileType === "text") {
      if (fileExtension === "csv") {
        return CSV_PlaceholderIcon;
      } else if (fileExtension === "pdf") {
        return PDF_PlaceholderIcon;
      } else if (fileExtension === "doc" || fileExtension === "docx") {
        return DOCX_PlaceholderIcon;
      } else if (fileExtension === "xls" || fileExtension === "xlsx") {
        return XLSX_PlaceholderIcon;
      }
    } else if (fileType === "image") {
      try {
        const blob = await DownloadAttachments(
          "Attachment",
          "Invoice",
          invoiceID,
          filename,
          navigate
        );
        const imgurl = URL.createObjectURL(blob);

        return imgurl;
      } catch (error) {
        console.error("Failed to fetch media thumbnail", error);
      }
    } else {
      return FilePlaceholder;
    }
  };

  const downloadAttachment = async (fileName) => {
    setLoading(true);
    try {
      const response = await DownloadAttachments(
        "Attachment",
        "Invoice",
        invoiceID,
        fileName,
        navigate
      );

      if (response && response.size > 0) {
        saveAs(response, fileName);

        toast.success(`File downloaded successfully: ${fileName}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      } else {
        toast.error(`Failed to download the file.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error downloading the file:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAttachment = async (mediaID) => {
    setLoading(true);
    try {
      const response = await DeleteAttachment(
        "Attachment",
        "Invoice",
        invoiceID,
        mediaID,
        navigate
      );

      if (!response.isError) {
        toast.success(`File deleted successfully`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchAttachments();
      } else {
        toast.error(`Failed to delete the file.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error deleting the file:", error);
    } finally {
      setLoading(false);
    }
  };
  //#endregion Handlers

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined) return '';

    const str = value.toString();

    // Allow partial numbers while typing (e.g. ".", "0.", ".5")
    if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
      return str;
    }

    // Remove commas and format integer part
    const parts = str.replace(/,/g, '').split('.');
    if (parts[0] !== '') {
      const num = Number(parts[0]);
      // Check if it's actually a valid number before formatting
      if (!isNaN(num)) {
        parts[0] = num.toLocaleString('en-US');
      }
    }

    return parts.join('.');
  };

  const removeCommas = (value) => value.replace(/,/g, '');
  
  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Invoices"}
      showIcon={true}
      NavURL={handlePageBack}
    >
      <Card sx={{ width: "100%", height: "100%", position: "relative" }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  rowGap: 2,
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {`INV-${
                    invoice?.invoiceID
                      ? String(invoice.invoiceID).padStart(3, "0")
                      : "000"
                  }`}
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: (4, 2) }}>
                      <Box>
                        <Typography
                          color="text.primary"
                          fontSize="small"
                          fontWeight="600"
                          mb={2}
                        >
                          Customer Details
                        </Typography>
                        <Box sx={{ display: "flex", gap: 20 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <Typography color="text.secondary" fontSize="small">
                              Customer Name
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Email
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Phone
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Invoice Create Date
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <Typography fontSize="small">
                              {invoice?.customerName || "N/A"}
                            </Typography>
                            <Typography fontSize="small">
                              {invoice?.customerEmail || "N/A"}
                            </Typography>
                            <Typography fontSize="small">
                              {invoice?.customerPhone || "N/A"}
                            </Typography>
                            <Typography fontSize="small">
                              {dayjs(invoice?.createDate).format(
                                "MM/DD/YYYY"
                              ) || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                >
                  <Box>
                    <CustomChip
                      sx={{ cursor: "pointer" }}
                      label={
                        <Typography variant="body1" color="primary">
                          {invoice?.customerName}
                        </Typography>
                      }
                      color="secondary"
                      skin={theme.palette.mode === "dark" ? 'dark' : 'light'}
                      icon={<Icon icon="tabler:building" />}
                    />
                  </Box>
                </HtmlTooltip>
                <CustomChipWithDropdown
                  tag={invoice?.status}
                  onSelect={(selectedTag) => {
                    setInvoice((prev) => ({
                      ...prev,
                      status: selectedTag,
                    }));
                  }}
                  hasInvoiceSavePermission={hasInvoiceSavePermission}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  width: { xs: "100%", sm: "auto" }, // Full-width buttons on mobile
                }}
              >
                <Button
                  variant="outlined"
                  color="main"
                  {...(hasInvoiceSavePermission && {
                    onClick: () => handleSaveInvoice(),
                  })}
                  disabled={!isChanged || !hasInvoiceSavePermission}
                >
                  Save Changes
                </Button>
                <Button
                  variant="contained"
                  color="main"
                  onClick={handleOpenGenerateInvoice}
                  disabled={isChanged}
                >
                  Preview {
                    hasInvoiceSendEmailPermission ?
                    invoice?.status === 'Sent' ?
                    '& Resend' :
                    "& Send" :
                    ""
                  }
                </Button>
              </Box>
            </Box>
          }
        />
        <CardContent sx={{ px: { xs: 1, sm: 4 }, py: 3 }}>
          {" "}
          {/* Reduce side padding on mobile */}
          <Box>
            <TabContext value={tabValue}>
              <TabList
                sx={{ mb: 2 }}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="false"
                aria-label="simple tabs example"
              >
                <Tab
                  value="1"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "1" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Invoice Details
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="2"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "2" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Terms and Conditions
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="3"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "3" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Attachments
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="4"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "4" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Payments
                      </Box>
                    </Box>
                  }
                />
              </TabList>
              <TabPanel value="1">
                <Grid container spacing={{ xs: 2, sm: 5 }}>
                  {" "}
                  <Grid item xs={12} sm={4} md={3}>
                    <FormLabel
                      sx={{
                        fontSize: "13px",
                        color: "text.primary",
                        mb: "4px",
                      }}
                    >
                      Work Order/Assessment ID{" "}
                      <span style={{ color: theme.palette.error.main }}>*</span>
                    </FormLabel>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.2)' : 'rgba(47, 43, 61, 0.2)'}`,
                        px: 2,
                        borderRadius: 1,
                        cursor: "default",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        {`${
                          invoice?.invoiceType === "Assessment"
                            ? "AST-"
                            : "WRO-"
                        }${
                          invoice?.requestID
                            ? String(invoice.requestID).padStart(3, "0")
                            : "000"
                        }`}
                      </Typography>
                      <Tooltip title="Update Work Order or Jobs">
                        <IconButton
                          disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                          // {...((hasInvoiceSavePermission || invoice?.status === 'Draft') && {
                          //   onClick: () => handleOpenSelectJobsDlg(),
                          // })}
                          onClick={handleOpenSelectJobsDlg}
                        >
                          <Icon icon="fluent-mdl2:search-and-apps" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: `1px solid #DDDDDD`,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          mx: 4,
                          my: 4,
                        }}
                      >
                        <Typography variant="h4">Invoice Details</Typography>
                      </Box>
                      <Divider sx={{ color: "text.disabled" }} />
                      <Grid
                        container
                        spacing={4}
                        sx={{
                          px: 4,
                          py: 6,
                        }}
                      >
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              justifyContent: "space-between",
                              border: "1px solid #DDDDDD",
                              borderRadius: 1,
                              p: 4,
                              gap: 3,
                            }}
                          >
                            {/* Left section: Customer, Billing Address, Phone & Email */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 2,
                                }}
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  Customer:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {invoice?.customerName}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 2,
                                }}
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  Billing Address:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {invoice?.billingAddress_Street1?.trim() ||
                                    invoice?.billingAddress?.trim() ||
                                    invoice?.companyAddress?.trim() ||
                                    invoice?.jobsiteAddress?.trim() ||
                                    "N/A"}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1.5,
                                }}
                              >
                                {/* Phone */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "baseline",
                                    gap: 3,
                                  }}
                                >
                                  <Typography variant="body1" fontWeight="bold">
                                    Phone:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {invoice?.customerPhone || "N/A"}
                                  </Typography>
                                </Box>

                                {/* Email */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    maxWidth: { xs: "100%", sm: "auto" },
                                  }}
                                >
                                  <Typography variant="body1" fontWeight="bold">
                                    Email:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {invoice?.customerEmail ||
                                      invoice?.email ||
                                      "N/A"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Right section: Jobsite info */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 2,
                                mt: { xs: 3, sm: 0 },
                              }}
                            >
                              <Typography variant="body1" fontWeight="bold">
                                Jobsite:
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {`${
                                  invoice?.jobsiteName ||
                                  invoice?.jobSiteName ||
                                  "N/A"
                                } - ${
                                  invoice?.jobsiteAddress ||
                                  invoice?.jobSiteAddress ||
                                  "N/A"
                                }`}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                          <CustomTextField
                            fullWidth
                            label="Invoice Title"
                            value={invoice?.invoiceTitle}
                            disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                            onChange={(e) =>
                              handleChangeInvoiceDetails(
                                "invoiceTitle",
                                e.target.value
                              )
                            }
                            placeholder="Enter Invoice Title"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                          <Box
                            sx={{
                              width: "100%",
                            }}
                          >
                            <CustomLabel label="Send By" required={false} />
                            <DatePopover
                              onChange={handleChangeInvoiceDetails}
                              invoiceDetails={invoice}
                              disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                            />
                          </Box>
                        </Grid>
                        {(invoice?.dueDate && invoice?.status === 'Sent') && (
                          <Grid item xs={12} sm={4} md={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              label="Payment Due"
                              value={dayjs(invoice?.dueDate).format('MM/DD/YYYY')}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={4} md={3}>
                          <CustomTextField
                            fullWidth
                            select
                            readOnly
                            SelectProps={{
                              multiple: true,
                              value: invoice?.paymentMethods || [],
                              onChange: (e) => {
                                if (e.target.value.includes("select-all")) {
                                  const allMethods = [
                                    "Cash",
                                    "Check",
                                    "Card",
                                    "BankTransfer",
                                    "ACH",
                                    "ApplePay",
                                    "GooglePay"
                                  ];
                                  if(invoice?.status === 'Sent'){
                                    return
                                  }else{
                                    handleChangeInvoiceDetails(
                                      "paymentMethods",
                                      invoice?.paymentMethods?.length ===
                                        allMethods.length
                                        ? []
                                        : allMethods
                                    );
                                  }
                                } else {
                                  if(invoice?.status === 'Sent'){
                                    return
                                  } else{
                                    handleChangeInvoiceDetails(
                                      "paymentMethods",
                                      e.target.value
                                    );
                                  }
                                }
                              },
                              renderValue: (selected) => {
                                if (selected.length === 0)
                                  return "Select Payment Methods";

                                return selected.join(", ");
                              },
                              displayEmpty: true,
                              MenuProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    backgroundColor: "transparent",
                                    "&.Mui-selected": {
                                      backgroundColor: "transparent",
                                      "&:hover": {
                                        backgroundColor: "action.hover",
                                      },
                                    },
                                  },
                                },
                              },
                            }}
                            required
                            label="Accepted Payment Methods"
                            error={!!errorsDetails.paymentMethods}
                            helperText={
                              !!errorsDetails.paymentMethods
                                ? "Select at least 1 payment method"
                                : ""
                            }
                          >
                            <MenuItem value="select-all">Select All</MenuItem>     
                            <MenuItem value="Card">Card</MenuItem>
                            <MenuItem value="ACH">ACH</MenuItem>              
                            <MenuItem value="BankTransfer">Bank Transfer</MenuItem>                
                            <MenuItem value="ApplePay">Apple Pay</MenuItem> 
                            <MenuItem value="GooglePay">Google Pay</MenuItem>  
                            <MenuItem value="Cash">Cash</MenuItem> 
                            <MenuItem value="Check">Check</MenuItem>         
                          </CustomTextField>
                        </Grid>
                        {(invoice?.invoiceType === "Assessment") && (
                          <Grid item xs={12} sm={4} md={3}>
                            <CustomTextField
                              fullWidth
                              required
                              disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                              label="Assessment Amount"
                              placeholder="0.00"
                              type="text" // <-- use text so commas are allowed
                              value={formatNumberWithCommas(invoice?.subTotal || 0)}
                              onChange={(e) => {
                                const rawValue = removeCommas(e.target.value); // strip commas
                                handleChangeInvoiceDetails("assessmentAmount", rawValue);
                                handleChangeInvoiceDetails("subTotal", rawValue);
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                              error={!!errorsDetails.assessmentAmount}
                              helperText={
                                !!errorsDetails.assessmentAmount ? "Amount must be greater than 0" : ""
                              }
                            />
                          </Grid>
                        )}
                        {(invoice?.invoiceType === "WorkOrderDeposit") && (
                          <Grid item xs={12} sm={4} md={3}>
                            <CustomTextField
                              fullWidth
                              required
                              readOnly
                              disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                              label="Deposit Amount"
                              placeholder="0.00"
                              value={formatNumberWithCommas(invoice?.subTotal || 0)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          {(invoice?.dueDate && (invoice?.status === 'Sent' || invoice?.status === 'Canceled')) ? (
                            <></>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              <Typography variant='body1'>
                                Payment Due:
                              </Typography>
                              <CustomTextField
                                fullWidth
                                required
                                disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                placeholder="10"
                                value={invoice?.paymentDueInterval}
                                onChange={(e) => {
                                  handleChangeInvoiceDetails(
                                    "paymentDueInterval",
                                    e.target.value
                                  );
                                }}
                                sx={{
                                  maxWidth: '50px'
                                }}
                              />
                              <Typography variant='body1'>
                                days after sent
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  {(invoice?.invoiceType === "WorkOrder") && (
                    <Grid item xs={12}>
                      <Typography variant="h4" mb={2}>
                        Invoice Items
                      </Typography>
                      <DataGridPro
                        autoHeight
                        hideFooter
                        rows={filteredRows || []}
                        columns={columns}
                        disableRowSelectionOnClick
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textTransform: "none",
                          },
                        }}
                        slots={{
                          footer: () => null
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4} md={3}>
                    <CustomTextField
                      fullWidth
                      select
                      disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                      label={
                        <Typography variant='body1' fontWeight='bold'>Sales Tax</Typography>
                      }
                      SelectProps={{
                        value: invoice?.county || "",
                        onChange: (e) => {
                          const value = e.target.value;

                          if (value === "add_new_county") {
                            e.preventDefault();
                            handleOpenCountyDlg();
                          } else {
                            const selectedCounty = salesTaxList.find(
                              (tax) => tax.county === value
                            );
                            handleChangeInvoiceDetails(
                              "salesTax",
                              selectedCounty.rate
                            );
                            handleChangeInvoiceDetails(
                              "county",
                              selectedCounty.county
                            );
                          }
                        },
                        renderValue: (selected) => {
                          if (!selected) return "Do not charge sales tax";

                          const selectedCounty = salesTaxList.find(
                            (tax) => tax.county === selected
                          );
                          return selectedCounty
                            ? `${selectedCounty.county} ( Sales Tax: ${selectedCounty.rate} )`
                            : "Do not charge sales tax";
                        },
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value="add_new_county">
                        + Add New County
                      </MenuItem>
                      {salesTaxList.length > 0 &&
                        salesTaxList.map((tax) => (
                          <MenuItem key={tax.salesTaxID} value={tax.county}>
                            {`${tax.county} ( Sales Tax: ${tax.rate}% )`}
                          </MenuItem>
                        ))}
                    </CustomTextField>
                  </Grid>
                  <Grid item xs={12}>
                   <Box sx={containerStyles}>
                      <Box sx={summaryStyles}>
                        <Box sx={summaryItemStyles}>
                          <Typography variant="body1" fontWeight="bold">
                            Total Amount
                          </Typography>
                          <Typography variant="body1">
                            $ {parseAmount(summary?.totalAmount || 0)}
                          </Typography>
                        </Box>
                        {invoiceType === "WorkOrderDeposit" ? (<></>) : (
                          <>
                            <Box sx={summaryItemStyles}>
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 0.5 } }}>
                                <Typography variant="body1" fontWeight="bold">
                                  Deposit Credit Available
                                </Typography>
                              </Box>
                              <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                                $ {parseAmount(advanceReceived || 0)}
                              </Typography>
                            </Box>
                            <Box sx={summaryItemStyles}>
                              <Typography variant="body1" fontWeight="bold">
                                Deposit Credit Utilized
                              </Typography>
                              {/* <Box sx={{ 
                                maxWidth: 100,
                                ml: 5 
                              }}>
                                <CustomTextField
                                  fullWidth
                                  placeholder="0.00"
                                  disabled={invoice?.status !== 'Draft'}
                                  value={invoice?.depositUtilizedAmount || 0}
                                  onChange={(e) => {
                                    handleChangeInvoice(
                                      "depositUtilizedAmount",
                                      e.target.value
                                    );
                                  }}
                                  size='small'
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        $
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Box> */}
                              <Typography variant="body1">
                                $ {parseAmount(summary?.depositCreditUtilized || 0)}
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              ...summaryItemStyles,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body1" fontWeight="bold">
                                Total Discount
                              </Typography>
                              <Box sx={{ 
                                maxWidth: 100,
                                ml: 5 
                              }}>
                                <CustomTextField
                                  fullWidth
                                  disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                  placeholder="0.00"
                                  value={invoice?.discountAmount}
                                  onChange={(e) => handleChangeInvoiceDetails("discountAmount", e.target.value)}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        $
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={!!errorsDetails.discountAmount}
                                  helperText={
                                    !!errorsDetails.discountAmount
                                      ? "Discount must not exceed total amount"
                                      : ""
                                  }
                                />
                              </Box>
                            </Box>
                          </>
                        )}
                        <Box sx={summaryItemStyles}>
                          <Typography variant="body1" fontWeight="bold">
                            Total Tax
                          </Typography>
                          <Typography variant="body1">
                            $ {parseAmount(summary?.totalTax || 0)}
                          </Typography>
                        </Box>

                        <Divider />
                        <Box sx={grandTotalStyles}>
                          <Typography variant="body1" fontWeight="inherit" color="inherit" fontSize="inherit">
                            Grand Total
                          </Typography>
                          <Typography variant="body1" fontWeight="inherit" color="inherit" fontSize="inherit">
                            $ {parseAmount(summary?.grandTotal || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h4" mb={2}>
                      Additional Options
                    </Typography>
                    <List component="nav" aria-label="main mailbox">
                      <ListItem
                        disablePadding
                        sx={{ borderBottom: "1px solid #E1E4EA" }}
                      >
                        <ListItemButton
                          onClick={() => setOpenNotes(!openNotes)}
                        >
                          <Icon
                            icon={
                              openNotes
                                ? "tabler:chevron-down"
                                : "tabler:chevron-right"
                            }
                          />
                          <ListItemText
                            sx={{ ml: 4 }}
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  Notes
                                </Typography>
                                <CustomChip
                                  label={invoice?.notes?.length || 0}
                                  skin="light"
                                  color="success"
                                />
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={openNotes} timeout="auto" unmountOnExit>
                        <Grid container spacing={3} mt={2}>
                          {invoice?.notes &&
                            invoice?.notes?.length > 0 &&
                            invoice?.notes?.map((note, index) => (
                              <Grid key={index} item xs={12}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    border: "1px solid #D0D5DD",
                                    borderRadius: 1,
                                    gap: 2,
                                    px: 2,
                                    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2">
                                      {index + 1}
                                      {`. `}
                                      {note.description}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <IconButton
                                      disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                      onClick={() =>
                                        handleDeleteNote(
                                          note.id || note.noteID,
                                          "edit"
                                        )
                                      }
                                    >
                                      <Icon icon="tabler:x" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <CustomTextField
                                fullWidth
                                disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                placeholder="Type any note"
                                sx={{
                                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431 !important' : "#fff !important",
                                }}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                              />
                              <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleAddNote("edit")}
                                disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                              >
                                <Icon icon="tabler:plus" />
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </List>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="2">
                <Grid container spacing={3} mt={2}>
                  {invoice?.additionalPaymentTerms &&
                    invoice?.additionalPaymentTerms?.length > 0 &&
                    invoice?.additionalPaymentTerms?.map(
                      (term, index) => (
                        <Grid key={index} item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              border: "1px solid #D0D5DD",
                              borderRadius: 1,
                              gap: 2,
                              px: 2,
                              backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#fff",
                            }}
                          >
                            <Box>
                              <Typography variant="body2">
                                {index + 1}
                                {`. `}
                                {term.paymentTerms}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={term.isActive}
                                    color="success"
                                    disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                    onChange={(e) =>
                                      handleChangeTerm(
                                        e.target.checked,
                                        "id" in term
                                          ? term.id
                                          : term.paymentTermID,
                                        "edit"
                                      )
                                    }
                                  />
                                }
                                label="Apply"
                              />
                              <IconButton
                                disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                                onClick={() =>
                                  handleDeleteTerm(
                                    "id" in term ? term.id : term.paymentTermID,
                                    "edit"
                                  )
                                }
                              >
                                <Icon icon="tabler:x" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid>
                      )
                    )}
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <CustomTextField
                        fullWidth
                        disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                        placeholder="Type your terms & conditions"
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#1B2431 !important' : "#fff !important",
                        }}
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                      />
                      <Button
                        variant="outlined"
                        color="secondary"
                        disabled={invoice?.status === 'Sent' || invoice?.status === 'Canceled' || invoice?.status === 'Paid' || invoice?.status === 'Partially Paid' || invoice?.status === 'Payment Past Due'}
                        onClick={() => handleAddTerm("edit")}
                      >
                        <Icon icon="tabler:plus" />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="3">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="main"
                    startIcon={<AddIcon />}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                    // disabled={!hasInvoiceSavePermission || invoice?.status !== 'Draft'}
                    {...(hasInvoiceSavePermission && {
                      onClick: () => handleOpenUploadDlg(),
                    })}
                  >
                    Add Attachment
                  </Button>
                </Box>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 6 },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                />
                <Timeline>
                  {attachments && attachments.length > 0 ? (
                    attachments.map((attachment, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                          {dayjs(attachment.createDate).format("MMMM DD, YYYY")}
                        </Typography>
                        <TimelineItem>
                          <TimelineSeparator>
                            <TimelineDot color="main" />
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent
                            sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "secondary", fontSize: "12px" }}
                            >
                              {dayjs(attachment.createDate).format("hh:mm A")}
                            </Typography>
                            <Box
                              sx={{
                                px: 6,
                                py: 4,
                                mt: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                border: "1px solid #D9D9D9",
                                borderRadius: 2,
                                width: { xs: '90%', sm: '40%' }, // Full width on mobile, 40% on larger screens
    maxWidth: { xs: 'none', sm: '40%' }, // Remove max-width constraint on mobile
    boxSizing: 'border-box' // Ensure padding is included in width
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <img
                                  width={50}
                                  height={50}
                                  alt={attachment.fileName || "attachment"}
                                  loading="lazy"
                                  src={
                                    thumbnails[attachment.fileName] ||
                                    "/images/ImageLoader.gif"
                                  }
                                />
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                  {attachment.fileName}
                                </Typography>
                              </Box>
                              {hasInvoiceSavePermission && (
                                <OptionsMenu
                                  iconButtonProps={{
                                    size: "small",
                                    sx: {
                                      color: "text.secondary",
                                      p: 0.3,
                                      borderRadius: "50%",
                                      "&:hover": {
                                        backgroundColor: (theme) =>
                                          theme.palette.action.hover,
                                        borderRadius: "50%",
                                      },
                                    },
                                  }}
                                  options={[
                                    {
                                      text: (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                          }}
                                        >
                                          <Icon icon="tabler:download" />
                                          Download
                                        </Box>
                                      ),
                                      menuItemProps: {
                                        onClick: () =>
                                          downloadAttachment(
                                            attachment.fileName
                                          ),
                                        sx: {
                                          color: "primary.main",
                                          "&:not(.Mui-focusVisible):hover": {
                                            color: "primary.main",
                                            backgroundColor: (theme) =>
                                              hexToRGBA(
                                                theme.palette.primary.main,
                                                0.08
                                              ),
                                          },
                                        },
                                      },
                                    },
                                    {
                                      text: (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                          }}
                                        >
                                          <Icon icon="tabler:trash" />
                                          Delete
                                        </Box>
                                      ),
                                      menuItemProps: {
                                        onClick: () =>
                                          deleteAttachment(attachment.mediaID),
                                        sx: {
                                          color: "error.main",
                                          "&:not(.Mui-focusVisible):hover": {
                                            color: "error.main",
                                            backgroundColor: (theme) =>
                                              hexToRGBA(
                                                theme.palette.error.main,
                                                0.08
                                              ),
                                          },
                                        },
                                      },
                                    },
                                  ]}
                                />
                              )}
                            </Box>
                          </TimelineContent>
                        </TimelineItem>
                      </Box>
                    ))
                  ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mx: "auto",
                            border: "1px solid #EAEAEA",
                            borderRadius: 1,
                            width: "100%",
                            height: 280,
                            py: 4,
                            alignItems: "center" ,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              justifyContent: "center",
                              alignItems: "center",
                              width: { xs: "90%", sm: "100%" }, 
                              maxWidth: 400, 
                              px: 2,
                              textAlign: "center" 
                            }}
                          >
                        <img
                          src={AddEmployeeDoodle}
                          alt="Empty State"
                          style={{ width: "100px", height: "auto" }}
                        />
                        <Typography variant="h6">No attachments added yet</Typography>
                      </Box>
                    </Box>
                  )}
                </Timeline>
              </TabPanel>
              <TabPanel value="4">
                <PaymentsPage invoiceID={invoiceID} invoiceStatus={invoice?.status} />
              </TabPanel>
            </TabContext>
          </Box>
        </CardContent>
      </Card>

      {/* Popup Dialogs */}
      <PreviewInvoiceDialog
        open={openGenerateInvoice}
        handleClose={handleCloseGenerateInvoice}
        toast={toast}
        navigate={navigate}
        setLoading={setLoading}
        invoiceID={invoiceID}
        previewData={invoice}
        terms={invoice?.additionalPaymentTerms}
        notes={invoice?.notes}
        fetchInvoice={fetchInvoice}
        invoiceType={invoiceType}
        invoiceJobs={filteredRows}
      />
      <SelectJobsDialog
        open={openSelectJobsDlg}
        handleClose={handleCloseSelectJobsDlg}
        JobSelectionProps={JobSelectionProps}
      />

      <AttachmentUpload
        open={openUploadDlg}
        handleClose={handleCloseUploadDlg}
        Title="Upload Attachment"
        files={uploadFiles}
        btnLoader={btnLoader}
        setUploadFiles={setUploadFiles}
        setFiles={setUploadFiles}
        setBtnLoader={setBtnLoader}
        navigate={navigate}
        entity="invoice"
        payload={{
          invoiceID: invoiceID,
          invoiceTitle: invoice?.invoiceTitle || "Invoice",
        }}
        setOpenUploadDlg={setOpenUploadDlg}
        fetchAttachments={fetchAttachments}
      />

      <JobDetailDialog
        open={openJobDetail}
        handleClose={handleCloseJobDetail}
        method={'detail'}
        entity={'workOrder-detail'} 
        rules={rules} 
        workReq={selectedWorkReq} 
        job={selectedJob}
        setJob={setSelectedJob}
        fetchWorkRequirements={fetchWorkRequirements}
        setLoading={setLoading} 
        toast={toast}
        fetchRequests={fetchRecords}
      />

      <AddSalesTaxDialog
        open={openCountyDlg}
        handleClose={handleCloseCountyDlg}
        onSave={handleAddCounty}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 10000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default InvoiceDetail;
